import { useCallback, useEffect, useRef, useState } from "react";
import type { TableConfig } from "@/lib/types";

/**
 * 場地平面圖：把桌子擺在對應真實餐廳格局的位置上。
 *
 * 座標是 0–1 的比例（見 types.ts 的 TableConfig.x/y），
 * 所以畫布不管多寬，桌子的相對位置都一樣。
 *
 * 畫布本身用固定的邏輯寬度 + 橫向捲動：
 * 手機上把 20 張桌子塞進 375px 會變成一堆看不清的小點，
 * 讓它捲動比硬縮小誠實得多。
 */

/**
 * 畫布的邏輯尺寸（px）。座標是比例值，所以畫布多大都不影響配置，
 * 差別只在桌子畫得多大、要不要橫向捲動。
 *
 * 後台編輯器用小畫布（桌子畫成精簡的圓片，好拖曳）；
 * 選位圖用大畫布（要看得到每個座位跟名字）。
 */
export const EDITOR_CANVAS = { w: 900, h: 600 };
// 選位圖的畫布跟著座位一起放大：座位是手指要點的目標，太小就點不到，
// 而桌子畫大之後畫布不跟著放大，桌子會互相疊在一起。
export const SEATMAP_CANVAS = { w: 1760, h: 900 };

/** 沒有座標的桌子要落在哪 —— 自動排成格線，之後可以再拖 */
export function autoGrid(tables: TableConfig[]): Record<string, { x: number; y: number }> {
  const cols = Math.ceil(Math.sqrt(tables.length)) || 1;
  const rows = Math.ceil(tables.length / cols) || 1;
  const out: Record<string, { x: number; y: number }> = {};
  tables.forEach((t, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    out[t.label] = {
      x: (col + 0.5) / cols,
      y: (row + 0.5) / rows,
    };
  });
  return out;
}

/** 這個活動是否已經排過平面配置 */
export function hasLayout(tables: TableConfig[]): boolean {
  return tables.some((t) => typeof t.x === "number" && typeof t.y === "number");
}

/** 取得每桌的座標：有存過就用存的，沒有就用自動格線補上 */
export function positionsOf(tables: TableConfig[]): Record<string, { x: number; y: number }> {
  const grid = autoGrid(tables);
  const out: Record<string, { x: number; y: number }> = {};
  for (const t of tables) {
    out[t.label] =
      typeof t.x === "number" && typeof t.y === "number" ? { x: t.x, y: t.y } : grid[t.label];
  }
  return out;
}

export interface FloorPlanProps {
  tables: TableConfig[];
  /** 每桌的座標；不給就自己算 */
  positions?: Record<string, { x: number; y: number }>;
  /** 可拖曳（後台編輯模式） */
  editable?: boolean;
  onMove?: (label: string, x: number, y: number) => void;
  /** 每桌要畫什麼 */
  renderTable: (table: TableConfig) => React.ReactNode;
  /** 每桌的寬度（px）—— 大桌畫大一點，一眼看得出哪桌坐得多 */
  tableWidth: (table: TableConfig) => number;
  canvas?: { w: number; h: number };
  /** 畫布下方的說明 */
  footer?: React.ReactNode;
  /**
   * 要捲到畫面中央並標示出來的桌號。
   * 桌子多的時候畫布會超出寬度，側邊清單靠這個把地圖帶到對的地方。
   */
  focusLabel?: string | null;
  /** 顯示縮放控制列 */
  zoomable?: boolean;
}

/** 縮放範圍：再小就看不出桌號，再大也沒有意義（桌子已經比手指大了） */
const MIN_ZOOM = 0.35;
const MAX_ZOOM = 1.5;
const ZOOM_STEP = 0.15;

const clampZoom = (z: number) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));

export function FloorPlan({
  tables,
  positions,
  editable = false,
  onMove,
  renderTable,
  tableWidth,
  canvas = EDITOR_CANVAS,
  footer,
  focusLabel,
  zoomable = false,
}: FloorPlanProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const tableRefs = useRef(new Map<string, HTMLDivElement>());
  const [dragging, setDragging] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [canPan, setCanPan] = useState(false);
  const [panning, setPanning] = useState(false);
  const pan = useRef<{
    x: number;
    y: number;
    left: number;
    top: number;
    moved: boolean;
  } | null>(null);
  // 拖曳結束時瀏覽器還會補送一次 click —— 那一下不能被當成選位
  const suppressClick = useRef(false);
  const pos = positions ?? positionsOf(tables);

  /**
   * 一開始就縮到「整個場地看得完」。
   *
   * 畫布的邏輯寬度是照「座位要大到點得到」訂的，塞不進手機、也塞不進
   * 選位頁右半邊的欄位 —— 一次只看得到四成，等於逼使用者盲目左右滑。
   * 先讓人看到全貌（要選位有左邊的清單），想在地圖上點再放大。
   *
   * 只在掛載時量一次：之後的縮放是使用者自己按的，
   * 視窗一變動就把他的縮放蓋掉會很煩。
   */
  const fitToWidth = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller || !scroller.clientWidth) return;
    setZoom(clampZoom(scroller.clientWidth / canvas.w));
  }, [canvas.w]);

  useEffect(() => {
    if (zoomable) fitToWidth();
  }, [zoomable, fitToWidth]);

  // 縮放之後畫面外還有東西嗎？沒有就別把游標變成「可以抓」的樣子
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    setCanPan(
      scroller.scrollWidth > scroller.clientWidth + 1 ||
        scroller.scrollHeight > scroller.clientHeight + 1,
    );
  }, [zoom, tables.length, canvas.w]);

  /**
   * 用滑鼠把地圖左右拖著看。
   *
   * 捲動容器本身只給觸控板與手指用 —— 滑鼠使用者沒有東西可以抓，
   * 放大之後就卡在畫面的某一段出不去（捲軸在 macOS 上還是隱藏的）。
   *
   * 只接滑鼠：觸控的左右滑由瀏覽器原生處理，攔下來只會比原生的差
   * （少了慣性與邊界回彈）。
   */
  const startPan = (e: React.PointerEvent) => {
    if (!zoomable || editable || e.pointerType !== "mouse") return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const overflows =
      scroller.scrollWidth > scroller.clientWidth || scroller.scrollHeight > scroller.clientHeight;
    if (!overflows) return;
    suppressClick.current = false;
    pan.current = {
      x: e.clientX,
      y: e.clientY,
      left: scroller.scrollLeft,
      top: scroller.scrollTop,
      moved: false,
    };
    setPanning(true);
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  };

  const movePan = (e: React.PointerEvent) => {
    const p = pan.current;
    const scroller = scrollerRef.current;
    if (!p || !scroller) return;
    const dx = e.clientX - p.x;
    const dy = e.clientY - p.y;
    // 幾 px 的抖動不算拖曳，否則點座位會變得很難點中
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) p.moved = true;
    scroller.scrollLeft = p.left - dx;
    scroller.scrollTop = p.top - dy;
  };

  const endPan = () => {
    suppressClick.current = !!pan.current?.moved;
    pan.current = null;
    setPanning(false);
  };

  const swallowClickAfterPan = (e: React.MouseEvent) => {
    if (!suppressClick.current) return;
    suppressClick.current = false;
    e.preventDefault();
    e.stopPropagation();
  };

  /**
   * 把某一桌捲到畫面中央。
   *
   * 兩個刻意的選擇：
   *
   * 1. 自己算 scrollLeft，不用 element.scrollIntoView —— 後者會連帶捲動所有
   *    祖先容器，把底部那條 sticky 的確認列推出畫面。這裡只動自己的橫向捲軸。
   * 2. 直接設值而不是 behavior:"smooth" —— 平滑捲動在這個結構下實測不會動
   *    （瀏覽器會在某些情況下整個略過動畫），而「點了清單卻沒反應」比
   *    「沒有動畫」糟糕得多。桌子上的高亮框會告訴使用者停在哪一桌。
   *
   * offsetLeft 是相對於 .floor-canvas（position:relative），而畫布本身就貼齊
   * 捲動容器的左緣。畫布是用 transform 縮放的，所以 offsetLeft 仍然是
   * 未縮放的座標 —— 要自己乘上 zoom 才會對到實際的捲動位置。
   */
  useEffect(() => {
    if (!focusLabel) return;
    const el = tableRefs.current.get(focusLabel);
    const scroller = scrollerRef.current;
    if (!el || !scroller) return;
    const target = (el.offsetLeft + el.offsetWidth / 2) * zoom - scroller.clientWidth / 2;
    scroller.scrollLeft = Math.max(
      0,
      Math.min(target, scroller.scrollWidth - scroller.clientWidth),
    );
  }, [focusLabel, zoom]);

  /**
   * 拖曳用 Pointer Events 而不是 mouse/touch 各寫一套 ——
   * setPointerCapture 讓指標移出桌子範圍後事件還是送得到這裡，
   * 不然拖快一點就會「掉針」。
   */
  const handlePointerDown = useCallback(
    (label: string) => (e: React.PointerEvent) => {
      if (!editable) return;
      e.preventDefault();
      (e.target as Element).setPointerCapture?.(e.pointerId);
      setDragging(label);
    },
    [editable],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging || !canvasRef.current) return;
      const r = canvasRef.current.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
      const y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
      onMove?.(dragging, x, y);
    },
    [dragging, onMove],
  );

  const endDrag = useCallback(() => setDragging(null), []);

  return (
    <div>
      {zoomable && (
        <div className="mb-2 flex items-center gap-1.5">
          <ZoomButton onClick={() => setZoom((z) => clampZoom(z - ZOOM_STEP))} disabled={zoom <= MIN_ZOOM} label="縮小">
            −
          </ZoomButton>
          <span className="text-sub w-12 text-center text-[12px] tabular-nums">
            {Math.round(zoom * 100)}%
          </span>
          <ZoomButton onClick={() => setZoom((z) => clampZoom(z + ZOOM_STEP))} disabled={zoom >= MAX_ZOOM} label="放大">
            ＋
          </ZoomButton>
          <button
            type="button"
            onClick={fitToWidth}
            className="btn btn-secondary ml-1 px-3 py-1.5 text-[12px]"
          >
            全部顯示
          </button>
        </div>
      )}

      <div
        ref={scrollerRef}
        onPointerDown={startPan}
        onPointerMove={movePan}
        onPointerUp={endPan}
        onPointerCancel={endPan}
        onClickCapture={swallowClickAfterPan}
        // 手機上地圖釘在畫面頂端，所以要有高度上限 ——
        // 不然放大之後它會佔滿整個螢幕，下面的清單完全看不到。
        // 超出的部分用拖曳（或手指滑）在這個觀景窗裡移動。
        // 桌機不釘也就不需要上限，地圖可以完整長開。
        className={`overflow-auto ${zoomable ? "max-h-[46vh] lg:max-h-none" : ""} ${
          canPan && zoomable && !editable ? (panning ? "cursor-grabbing" : "cursor-grab") : ""
        }`}
      >
        {/*
          外層用縮放後的尺寸撐開捲動範圍：transform 不影響版面配置，
          少了這一層，捲軸會照未縮放的寬度算 —— 縮小之後右邊會多出一片空白，
          放大之後則捲不到最右邊那幾桌。
        */}
        <div style={{ width: canvas.w * zoom, height: canvas.h * zoom }}>
        <div
          ref={canvasRef}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          className="floor-canvas relative"
          style={{
            width: canvas.w,
            height: canvas.h,
            transform: `scale(${zoom})`,
            transformOrigin: "top left",
          }}
        >
          {tables.map((t) => {
            const p = pos[t.label] ?? { x: 0.5, y: 0.5 };
            const size = tableWidth(t);
            return (
              <div
                key={t.label}
                ref={(el) => {
                  if (el) tableRefs.current.set(t.label, el);
                  else tableRefs.current.delete(t.label);
                }}
                onPointerDown={handlePointerDown(t.label)}
                className={`absolute rounded-[20px] ${editable ? "cursor-grab active:cursor-grabbing" : ""} ${
                  dragging === t.label ? "z-10 opacity-90" : ""
                } ${focusLabel === t.label ? "ring-accent z-10 ring-2 ring-offset-2" : ""}`}
                style={{
                  left: `${p.x * 100}%`,
                  top: `${p.y * 100}%`,
                  width: size,
                  transform: "translate(-50%, -50%)",
                  touchAction: editable ? "none" : undefined,
                }}
              >
                {renderTable(t)}
              </div>
            );
          })}
        </div>
        </div>
      </div>
      {footer}
    </div>
  );
}

function ZoomButton({
  onClick,
  disabled,
  label,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="btn btn-secondary h-8 w-8 p-0 text-[15px] leading-none disabled:opacity-40"
    >
      {children}
    </button>
  );
}
